"use client";

import { useEffect, useMemo, useState } from "react";
import { useAuthStore } from "@/store/auth.store";
import { CustomerOrder } from "@/types";
import { api } from "@/lib/api";
import { formatCurrency, formatDate, ORDER_STATUS_LABELS } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import Link from "next/link";
import {
  ArrowRight,
  CalendarDays,
  ChevronRight,
  Clock,
  Heart,
  MapPin,
  Package,
  ReceiptText,
  ShoppingBag,
  TrendingUp,
  User,
} from "lucide-react";
import {
  AccountEmptyState,
  AccountMetric,
  AccountPageHeader,
  AccountSectionHeader,
  AccountSurface,
  OrderStatusTimeline,
} from "@/components/account/account-ui";

type BadgeVariant = "success" | "info" | "warning" | "error" | "status";

const STATUS_BADGE_VARIANT: Record<string, BadgeVariant> = {
  Pending: "warning",
  Processing: "info",
  Shipped: "info",
  Delivered: "success",
  Cancelled: "error",
  Refunded: "status",
  OnHold: "warning",
};

const ACTIVE_STATUSES = new Set(["Pending", "Processing", "Shipped", "OnHold"]);

const QUICK_ACTIONS = [
  {
    href: "/account/profile",
    label: "Profil bilgileri",
    description: "İsim, telefon ve şifre",
    icon: User,
  },
  {
    href: "/account/addresses",
    label: "Adres defteri",
    description: "Teslimat adresleri",
    icon: MapPin,
  },
  {
    href: "/account/wishlist",
    label: "Favoriler",
    description: "Kaydettiğiniz ürünler",
    icon: Heart,
  },
];

export default function AccountPage() {
  const { customer } = useAuthStore();
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customer) return;
    api
      .get<{ items: CustomerOrder[] }>("/api/account/orders")
      .then((res) => setOrders(res.data?.items ?? []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [customer]);

  const orderSummary = useMemo(
    () =>
      orders.reduce(
        (summary, order) => {
          const isActive = ACTIVE_STATUSES.has(order.status);
          return {
            totalSpent:
              order.status === "Cancelled" || order.status === "Refunded"
                ? summary.totalSpent
                : summary.totalSpent + order.total,
            activeOrders: isActive ? summary.activeOrders + 1 : summary.activeOrders,
            activeOrder: summary.activeOrder ?? (isActive ? order : null),
          };
        },
        { totalSpent: 0, activeOrders: 0, activeOrder: null as CustomerOrder | null }
      ),
    [orders]
  );

  if (!customer) return null;

  const firstName = customer.fullName.split(" ")[0];

  const recentOrders = orders.slice(0, 5);

  return (
    <div className="space-y-8">
      <AccountPageHeader
        eyebrow="Müşteri hesabı"
        title={`Merhaba, ${firstName}`}
        description="Siparişlerinizi, adreslerinizi ve kaydettiğiniz ürünleri tek ekrandan takip edin."
        action={
          <Link
            href="/products"
            className="account-primary-action inline-flex min-h-11 items-center gap-2 rounded-md px-4 text-sm font-bold transition duration-200"
          >
            Alışverişe devam et
            <ArrowRight className="h-4 w-4" />
          </Link>
        }
      />

      {loading ? (
        <Skeleton className="h-44 rounded-lg" />
      ) : orderSummary.activeOrder ? (
        <AccountSurface className="overflow-hidden">
          <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="p-5 sm:p-6">
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--es-text-muted)]">
                    Aktif sipariş
                  </p>
                  <h2 className="mt-2 text-xl font-bold text-[var(--sf-foreground)]">
                    #{orderSummary.activeOrder.orderNumber}
                  </h2>
                  <p className="mt-1 text-sm text-[var(--es-text-muted)]">
                    {formatDate(orderSummary.activeOrder.createdAt)} · {orderSummary.activeOrder.itemCount} ürün
                  </p>
                </div>
                <Badge variant={STATUS_BADGE_VARIANT[orderSummary.activeOrder.status] ?? "status"}>
                  {ORDER_STATUS_LABELS[orderSummary.activeOrder.status]}
                </Badge>
              </div>
              <OrderStatusTimeline status={orderSummary.activeOrder.status} />
            </div>
            <div className="border-t border-[var(--es-border-soft)] p-5 lg:border-l lg:border-t-0">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--es-text-muted)]">
                Sipariş toplamı
              </p>
              <p className="mt-2 text-2xl font-bold text-[var(--sf-foreground)]">
                {formatCurrency(orderSummary.activeOrder.total)}
              </p>
              <Link
                href={`/account/orders/${orderSummary.activeOrder.id}`}
                className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-[var(--sf-accent)] px-4 text-sm font-bold text-black transition duration-200 hover:bg-[var(--sf-accent-hover)]"
              >
                Siparişi incele
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </AccountSurface>
      ) : orders.length > 0 ? (
        <AccountSurface className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--es-text-muted)]">
              Sipariş durumu
            </p>
            <h2 className="mt-2 text-xl font-bold text-[var(--sf-foreground)]">
              Aktif siparişiniz yok
            </h2>
            <p className="mt-1 text-sm text-[var(--es-text-muted)]">
              Son siparişleriniz aşağıda listeleniyor.
            </p>
          </div>
          <Link
            href="/products"
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[var(--es-border)] px-4 text-sm font-bold text-[var(--sf-foreground)] transition duration-200 hover:border-[var(--sf-accent)] hover:text-[var(--sf-accent)]"
          >
            Yeni ürünlere bak
            <ArrowRight className="h-4 w-4" />
          </Link>
        </AccountSurface>
      ) : null}

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-3">
          <AccountMetric
            icon={ShoppingBag}
            label="Toplam Sipariş"
            value={String(orders.length)}
            helper={orders.length === 0 ? "Henüz yok" : `${orders.length} sipariş`}
          />
          <AccountMetric
            icon={TrendingUp}
            label="Toplam Harcama"
            value={formatCurrency(orderSummary.totalSpent)}
            tone="accent"
          />
          <AccountMetric
            icon={Clock}
            label="Aktif Sipariş"
            value={String(orderSummary.activeOrders)}
            helper={orderSummary.activeOrders > 0 ? "Devam ediyor" : "Bekleyen yok"}
            tone={orderSummary.activeOrders > 0 ? "success" : "default"}
          />
        </div>
      )}

      <section>
        <AccountSectionHeader
          title="Son siparişler"
          description="Sipariş durumlarını ve detaylarını hızlıca kontrol edin."
        />
        {loading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
        ) : recentOrders.length === 0 ? (
          <AccountEmptyState
            icon={Package}
            title="Henüz siparişiniz yok"
            description="İlk siparişinizden sonra kargo durumu, fatura ve tekrar sipariş aksiyonları burada görünecek."
            href="/products"
            actionLabel="Ürünleri incele"
          />
        ) : (
          <AccountSurface className="overflow-hidden">
            {recentOrders.map((order) => (
              <Link
                key={order.id}
                href={`/account/orders/${order.id}`}
                className="group grid gap-3 border-b border-[var(--es-border-soft)] px-4 py-4 transition duration-200 last:border-b-0 hover:bg-[color-mix(in_srgb,var(--sf-foreground)_4%,transparent)] sm:grid-cols-[1fr_auto] sm:items-center sm:px-5"
              >
                <div className="flex min-w-0 gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--sf-foreground)_6%,transparent)] text-[var(--sf-foreground)]">
                    <ReceiptText className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-bold text-[var(--sf-foreground)]">
                        #{order.orderNumber}
                      </span>
                      <Badge variant={STATUS_BADGE_VARIANT[order.status] ?? "status"}>
                        {ORDER_STATUS_LABELS[order.status]}
                      </Badge>
                    </div>
                    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--es-text-muted)]">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {formatDate(order.createdAt)}
                      </span>
                      <span>{order.itemCount} ürün</span>
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 sm:justify-end">
                  <span className="text-sm font-bold text-[var(--sf-foreground)]">
                    {formatCurrency(order.total)}
                  </span>
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--es-border-soft)] text-[var(--es-text-muted)] transition duration-200 group-hover:border-[var(--sf-accent)] group-hover:text-[var(--sf-accent)]">
                    <ChevronRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </AccountSurface>
        )}
      </section>

      <section>
        <AccountSectionHeader title="Hızlı işlemler" />
        <div className="grid gap-3 md:grid-cols-3">
          {QUICK_ACTIONS.map((action) => (
            <Link
              key={action.href}
              href={action.href}
              className="group rounded-lg border border-[var(--es-border-soft)] bg-[var(--sf-card-bg,var(--es-surface,#fff))] p-4 transition duration-200 hover:-translate-y-0.5 hover:border-[var(--sf-accent)] hover:shadow-[var(--es-shadow-sm)]"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--sf-foreground)_6%,transparent)] text-[var(--sf-foreground)] transition duration-200 group-hover:bg-[var(--sf-accent)] group-hover:text-black">
                  <action.icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[var(--sf-foreground)]">{action.label}</p>
                  <p className="mt-1 text-xs text-[var(--es-text-muted)]">{action.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
